import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Droplets, Wind, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITIES = [
  { slug: 'cakovec', name: 'Čakovec' },
  { slug: 'prelog', name: 'Prelog' },
  { slug: 'mursko-srediste', name: 'Mursko Središće' },
  { slug: 'varazdin', name: 'Varaždin' },
  { slug: 'zagreb', name: 'Zagreb' },
];

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('hr-HR', { weekday: 'short', day: 'numeric', month: 'numeric' });
  } catch { return iso; }
}

export default function WeatherPage() {
  const [city, setCity] = useState('cakovec');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/public/weather?city=${city}`).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [city]);

  return (
    <>
      <Helmet><title>Vrijeme u Međimurju — E-MINA</title></Helmet>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="weather-page">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Međimurje vrijeme</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Prognoza za Čakovec i okolicu — izvor: Open-Meteo.</p>

        {/* City tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {CITIES.map(c => (
            <button key={c.slug} onClick={() => setCity(c.slug)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${city === c.slug ? 'bg-red-600 text-white border-red-600' : 'bg-white dark:bg-[#0f172a] text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-red-400'}`}
              data-testid={`weather-city-${c.slug}`}>
              {c.name}
            </button>
          ))}
        </div>

        {loading && <p className="text-gray-400">Učitavam…</p>}
        {data?.error && <p className="text-red-500">Greška: {data.error}</p>}

        {data && !data.error && (
          <>
            {/* Current hero */}
            <div className="bg-gradient-to-br from-sky-50 to-sky-100 dark:from-[#0b1220] dark:to-[#111827] border border-sky-100 dark:border-gray-800 rounded-2xl p-8 mb-8 shadow-sm" data-testid="weather-current">
              <div className="flex items-center gap-2 text-sm text-sky-700 dark:text-sky-400 mb-3"><MapPin size={14} /> {data.city}</div>
              <div className="flex flex-wrap items-end gap-6">
                <div className="text-8xl leading-none" data-testid="weather-current-emoji">{data.current.emoji}</div>
                <div>
                  <div className="text-6xl font-bold text-gray-900 dark:text-gray-100" data-testid="weather-current-temp">{data.current.temp_c}°<span className="text-3xl font-normal text-gray-500">C</span></div>
                  <p className="text-lg text-gray-700 dark:text-gray-200 mt-1">{data.current.condition_label_hr}</p>
                </div>
                <div className="flex gap-5 text-sm text-gray-600 dark:text-gray-300 ml-auto">
                  <span className="flex items-center gap-1.5"><Droplets size={14} /> {data.current.humidity}%</span>
                  <span className="flex items-center gap-1.5"><Wind size={14} /> {data.current.wind_kmh} km/h</span>
                </div>
              </div>
            </div>

            {/* 7-day forecast */}
            <h2 className="text-base font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100 border-l-4 border-sky-500 pl-3 mb-4">Prognoza 7 dana</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="weather-forecast">
              {data.forecast_7d.map((d, i) => (
                <div key={i} className="bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl p-4 text-center hover:shadow-md transition-shadow" data-testid={`weather-day-${i}`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{fmtDate(d.date)}</p>
                  <div className="text-4xl my-3">{d.emoji}</div>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">{d.condition}</p>
                  <p className="text-sm"><span className="font-bold text-gray-900 dark:text-gray-100">{d.temp_max}°</span> <span className="text-gray-400">/ {d.temp_min}°</span></p>
                  <p className="text-[10px] text-sky-600 dark:text-sky-400 mt-1">💧 {d.precip_pct}%</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
